import { cssVariables, isDefined } from "@/utils/utils";
import * as am5 from "@amcharts/amcharts5";
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { Card, Flex } from "antd";
import { t } from "i18next";
import { useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useGetDashboardMlmStructure } from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { formatAgentIdNumber, formatAgentName } from "../../../../agent/utils";
import { useHasPermissions } from "../../../../auth/hooks";
import { formatCommissionsLevelName } from "../../../../commissions/level/utils";
import { getCommissionsSettingsRankColor } from "../../../../commissions/settings/utils";
import { AgentMlmNode, AgentMlmWithSubordinates } from "../../../types";

const ROOT_SIZE = 60;
const SUBORDINATES_SIZE = 45;
const OTHER_SIZE = 35;

const GOLD_COLOR = am5.color("#ffce2b");
const BASIC_COLOR = am5.color("#e1e1e1");

type MlmTreeNode = {
  name: string;
  radius: number;
  children: MlmTreeNode[];
  idNumber?: string;
  linkColor?: am5.Color;
  tooltipText?: string;
  circleColor?: am5.Color;
  circleBorderColor?: am5.Color;
  outerCircleColor?: am5.Color;
  url?: string;
  strokeWidth?: number;
};

const getMlmTreeNode = (
  node: Omit<AgentMlmNode, "hasSubordinates">,
  url: string,
  isCovered: boolean,
  radius: number,
  children?: MlmTreeNode[],
): MlmTreeNode => {
  return {
    name: node.aggregatedName,
    radius,
    url,
    idNumber: formatAgentIdNumber(node),
    tooltipText: [
      node.representatives?.length
        ? `${t("dashboard.mlmStructure.helpers.representatives")}: ${node.representatives.map((representative) => formatAgentName(representative)).join(", ")}`
        : undefined,
      node.commissionsSettings.level
        ? `${t("agent.helpers.commissionsSettingsLevel")}: ${formatCommissionsLevelName(node.commissionsSettings.level)}`
        : undefined,
      node.commissionsSettings.rank
        ? `${t("agent.helpers.commissionsSettingsRank")}: ${node.commissionsSettings.rank.name}`
        : undefined,
    ]
      .filter((item) => !!item || item?.length === 0)
      .join(" | "),
    circleColor: am5.color(getCommissionsSettingsRankColor(node.commissionsSettings.rank)),
    linkColor: isCovered ? GOLD_COLOR : undefined,
    outerCircleColor: isCovered ? GOLD_COLOR : undefined,
    circleBorderColor: isCovered ? GOLD_COLOR : undefined,
    strokeWidth: isCovered ? 3 : 1,
    children: children ?? [],
  };
};

const emptyCircle = {
  name: "",
  circleColor: "#fff",
  radius: OTHER_SIZE,
  children: [],
  tooltipText: t("dashboard.mlmStructure.helpers.emptyStructure"),
};

const calculateIsCovered = (agent: AgentMlmWithSubordinates) =>
  isDefined(agent.commissionsSettings.level?.targetSubordinatesCount) &&
  agent.subordinates.length >= agent.commissionsSettings.level?.targetSubordinatesCount;

type BuildAgentTreeParams = {
  agent: AgentMlmWithSubordinates;
  isCovered: boolean;
};

const buildMlmTree = ({ agent, isCovered }: BuildAgentTreeParams): MlmTreeNode => {
  const subs = agent.subordinates ?? [];
  const limit = 10;

  const visibleSubs = subs
    .slice(0, limit)
    .map((sub) =>
      getMlmTreeNode(sub, generatePath(AGENT_ROUTE_PATHS.detail.to, { id: sub.id }), isCovered, SUBORDINATES_SIZE),
    );

  const hiddenCount = subs.length - limit;

  const children =
    hiddenCount > 0
      ? [
          ...visibleSubs,
          {
            name: `+${hiddenCount} ${t("dashboard.mlmStructure.helpers.more")}`,
            children: [],
            url: generatePath(AGENT_ROUTE_PATHS.tree.to),
            radius: OTHER_SIZE,
          },
        ]
      : visibleSubs;

  return getMlmTreeNode(agent, generatePath(AGENT_ROUTE_PATHS.tree.to), isCovered, ROOT_SIZE, children);
};

export const DashboardMlmStructureWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasReadAgentPermission = useHasPermissions(Permission.AGENT_READ);

  const { data } = useGetDashboardMlmStructure();

  const rootAgentData = useMemo(
    () =>
      data
        ? buildMlmTree({
            agent: data,
            isCovered: calculateIsCovered(data),
          })
        : undefined,
    [data],
  );

  const emptySubordinatesCount = useMemo(() => {
    if (isDefined(data?.commissionsSettings.level?.targetSubordinatesCount)) {
      return data.commissionsSettings.level?.targetSubordinatesCount - data.subordinates.length;
    }

    return 0;
  }, [data]);

  useLayoutEffect(() => {
    if (!rootAgentData) {
      return;
    }

    const root = am5.Root.new("chartdiv");

    root.setThemes([am5themes_Animated.new(root)]);

    const container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: root.verticalLayout,
      }),
    );

    const series = container.children.push(
      am5hierarchy.ForceDirected.new(root, {
        singleBranchOnly: false,
        topDepth: 1,
        downDepth: 1,
        initialDepth: 1,
        fillField: "circleColor",
        categoryField: "name",
        childDataField: "children",
        centerStrength: 0.6,
      }),
    );

    series.nodes.template.setAll({ toggleKey: "none", cursorOverStyle: "default" });

    series.nodes.template.setup = (target) => {
      target.events.once("dataitemchanged", (ev) => {
        const node = ev.target;
        const context = node.dataItem?.dataContext as any;

        node.setAll({ cursorOverStyle: "pointer" });

        if (context.url && hasReadAgentPermission) {
          node.events.on("click", () => {
            navigate(context.url);
          });
        }
      });
    };

    series.links.template.adapters.add("strokeWidth", (defaultStrokeWidth, target) => {
      const context = target.get("target")?.dataContext as any;

      return context.strokeWidth ?? defaultStrokeWidth;
    });

    series.links.template.adapters.add("stroke", (_, target) => {
      const context = target.get("target")?.dataContext as any;

      return context.linkColor ?? am5.color(cssVariables.colorGrey);
    });

    series.circles.template.adapters.add("stroke", (defaultCircleBorderColor, target) => {
      const context = target.dataItem?.dataContext as any;

      return context.circleBorderColor ?? defaultCircleBorderColor;
    });

    series.circles.template.setAll({ strokeOpacity: 1, stroke: BASIC_COLOR });

    series.circles.template.adapters.add("strokeWidth", (defaultStrokeWidth, target) => {
      const context = target.dataItem?.dataContext as any;

      return context.strokeWidth ?? defaultStrokeWidth;
    });

    series.circles.template.adapters.add("radius", (defaultRadius, target) => {
      const context = target.dataItem?.dataContext as any;

      return context.radius ?? defaultRadius;
    });

    series.outerCircles.template.adapters.add("stroke", (defaultStroke, target) => {
      const context = target.dataItem?.dataContext as any;

      return context.outerCircleColor ?? defaultStroke;
    });

    series.outerCircles.template.setAll({ strokeWidth: 2 });

    series.labels.template.setAll({
      fontSize: 18,
      fill: am5.color(cssVariables.colorText),
      text: `{name}\n[fontSize: 12px]{idNumber}[/]`,
      textBaseline: "middle",
      textAlign: "center",
    });

    const tooltip = am5.Tooltip.new(root, { labelText: "[#1e1e1e]{tooltipText}[/]" });

    tooltip.get("background")?.setAll({ stroke: BASIC_COLOR, strokeOpacity: 0.8 });

    series.set("tooltip", tooltip);

    const emptyCircles = Array.from({ length: emptySubordinatesCount }, () => emptyCircle);

    series.data.setAll([{ value: 0, children: [rootAgentData, ...emptyCircles] }]);

    series.set("selectedDataItem", series.dataItems[0]);

    series.appear(1500, 200);

    return () => {
      root.dispose();
    };
  }, [rootAgentData, emptySubordinatesCount]);

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Card style={{ borderRadius: 18, height: "100%" }} styles={{ body: { height: "100%", padding: 8 } }}>
        <h2
          style={{ fontWeight: 600, fontSize: 28, position: "absolute" }}
          className="margin-left-small margin-top-tiny"
        >
          {t("dashboard.mlmStructure.title")}
        </h2>
        <div id="chartdiv" style={{ width: "100%", height: "100%" }}></div>
      </Card>
    </Flex>
  );
};
